// src/services/companyService.js
import { edefterFetch } from './edefterApi';

const EDEFTER_API_URL = window.APP_CONFIG?.edefterApiUrl || 'https://srv.dpg.net.tr/edefter';

class CompanyService {
  /**
   * Create a new company
   * @param {Object} companyData - Company data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async createCompany(companyData) {
    try {
      console.log('CompanyService.createCompany - Request data:', companyData); // Debug log
      
      const response = await edefterFetch(`${EDEFTER_API_URL}/api/company/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyData),
      });

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text || 'Unknown error';
      }

      console.log('CompanyService.createCompany - Response status:', response.status); // Debug log
      console.log('CompanyService.createCompany - Response data:', data); // Debug log

      if (response.ok) {
        return {
          success: true,
          data: data,
        };
      } else {
        // Handle error response - could be string or object
        let errorMessage = 'Failed to create company.';
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          errorMessage = data.message || data.error || data.status || JSON.stringify(data);
        }
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (error) {
      console.error('Create company error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create company.',
      };
    }
  }

  /**
   * Get companies by user ID
   * @param {string} userId - User ID
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  static async getCompaniesByUserId(userId) {
    try {
      const response = await edefterFetch(`${EDEFTER_API_URL}/api/company/list?userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          error: 'Failed to load companies.',
        };
      }
    } catch (error) {
      console.error('Get companies error:', error);
      return {
        success: false,
        error: error.message || 'Failed to load companies.',
      };
    }
  }

  /**
   * Get company by ID
   * @param {string} companyId - Company ID
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async getCompanyById(companyId) {
    try {
      const response = await edefterFetch(`${EDEFTER_API_URL}/api/company/${companyId}`);
      
      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          data: data,
        };
      } else if (response.status === 404) {
        return {
          success: false,
          error: 'Company not found.',
        };
      } else {
        return {
          success: false,
          error: 'Failed to load company.',
        };
      }
    } catch (error) {
      console.error('Get company by id error:', error);
      return {
        success: false,
        error: error.message || 'Failed to load company.',
      };
    }
  }

  /**
   * Update company
   * @param {string} companyId - Company ID
   * @param {Object} companyData - Updated company data
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async updateCompany(companyId, companyData) {
    try {
      const response = await edefterFetch(`${EDEFTER_API_URL}/api/company/${companyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(companyData),
      });

      const data = await response.json();

      if (response.ok) {
        return {
          success: true,
          data: data,
        };
      } else {
        return {
          success: false,
          error: data || 'Failed to update company.',
        };
      }
    } catch (error) {
      console.error('Update company error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update company.',
      };
    }
  }
}

export default CompanyService;

